import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CategoryPicker, LinkPicker, ListInput, PersonSelect } from "./ResourceFormFields";
import { errText } from "./resourceApi";

const ACCEPT = "application/pdf,.docx,image/png,image/jpeg";

const initial = (r, audience, me) => ({
  title: r?.title || "", description: r?.description || "", audience: r?.audience || audience || "client",
  type: r?.type || "educational_handout", categories: r?.categories || [], tags: r?.tags || [],
  programs: r?.programs || [], owner_id: r?.owner_id || me?.id || null, clinical_reviewer_id: r?.clinical_reviewer_id || null,
  restrictions: r?.restrictions || "", next_review_date: r?.next_review_date || "",
  reception_can_send: r?.reception_can_send ?? true, links: (r?.links || []).map(({ kind, ref_id }) => ({ kind, ref_id })),
});

/** Create a resource (optionally with its first file) or edit an existing resource's metadata. */
export function ResourceFormDialog({ meta, resource, defaultAudience, onClose, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState(() => initial(resource, defaultAudience, user));
  const locked = !!resource?.current_version_id;
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const linkOnly = meta.types.find((t) => t.key === form.type)?.link_only;

  async function save() {
    setSaving(true);
    try {
      const body = { ...form, next_review_date: form.next_review_date || null };
      if (!body.owner_id) delete body.owner_id;
      const r = resource ? await api.patch(`/clinical-resources/${resource.id}`, body)
        : await api.post("/clinical-resources", body);
      if (!resource && file && !linkOnly) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("change_summary", "First version");
        await api.post(`/clinical-resources/${r.data.id}/versions`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(resource ? "Resource updated" : "Resource added as a draft");
      onSaved(r.data);
    } catch (e) {
      toast.error("Could not save", { description: errText(e) });
    } finally {
      setSaving(false);
    }
  }

  const canSave = form.title.trim().length >= 3 && form.categories.length > 0;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="resource-form-dialog">
        <DialogHeader>
          <DialogTitle>{resource ? "Edit resource details" : "Add a resource"}</DialogTitle>
          <DialogDescription>
            {resource ? "Changes are recorded in the audit log." : "New resources start as a Draft and need a clinical approver before they can be sent to clients."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label className="text-xs">Title</Label>
            <Input className="mt-1" value={form.title} onChange={(e) => set({ title: e.target.value })} data-testid="resource-form-title" /></div>
          <div><Label className="text-xs">Description</Label>
            <Textarea className="mt-1" rows={3} value={form.description} onChange={(e) => set({ description: e.target.value })}
                      data-testid="resource-form-description" /></div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label className="text-xs">Audience</Label>
              <Select value={form.audience} onValueChange={(v) => set({ audience: v })} disabled={locked}>
                <SelectTrigger className="mt-1" data-testid="resource-form-audience"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Client resource</SelectItem>
                  <SelectItem value="practitioner">Practitioner resource</SelectItem>
                </SelectContent>
              </Select></div>
            <div><Label className="text-xs">Type</Label>
              <Select value={form.type} onValueChange={(v) => set({ type: v })} disabled={locked || !!resource?.version_count}>
                <SelectTrigger className="mt-1" data-testid="resource-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.types.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          {locked && <div className="-mt-2 text-2xs text-zinc-500">Audience and type are fixed once a version is approved.</div>}
          <div><Label className="text-xs">Categories</Label>
            <div className="mt-1"><CategoryPicker categories={meta.categories} value={form.categories} onChange={(v) => set({ categories: v })} /></div></div>
          <LinkPicker meta={meta} links={form.links} onChange={(v) => set({ links: v })} required={linkOnly} />
          <div className="grid gap-3 sm:grid-cols-2">
            <ListInput label="Tags (comma separated)" value={form.tags} onChange={(v) => set({ tags: v })}
                       suggestions={meta.tags} testId="resource-form-tags" />
            <ListInput label="Applicable programs" value={form.programs} onChange={(v) => set({ programs: v })}
                       suggestions={meta.programs} testId="resource-form-programs" />
            <PersonSelect label="Resource owner" value={form.owner_id} onChange={(v) => set({ owner_id: v })}
                          people={meta.staff} testId="resource-form-owner" />
            <PersonSelect label="Clinical reviewer" value={form.clinical_reviewer_id} allowNone
                          onChange={(v) => set({ clinical_reviewer_id: v })} people={meta.approvers} testId="resource-form-reviewer" />
            <div><Label className="text-xs">Next review date</Label>
              <Input type="date" className="mt-1" value={form.next_review_date} onChange={(e) => set({ next_review_date: e.target.value })}
                     data-testid="resource-form-next-review" /></div>
          </div>
          <div><Label className="text-xs">Restrictions / conditions of use</Label>
            <Textarea className="mt-1" rows={2} value={form.restrictions} onChange={(e) => set({ restrictions: e.target.value })}
                      placeholder="e.g. Only with individualised safety instructions" data-testid="resource-form-restrictions" /></div>
          {form.audience === "client" && (
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.reception_can_send} onCheckedChange={(v) => set({ reception_can_send: v })} disabled={!meta.perms.manage}
                      data-testid="resource-form-reception" /> Reception may send this resource to clients
            </label>
          )}
          {!resource && !linkOnly && (
            <div><Label className="text-xs">File (PDF, Word .docx, PNG or JPEG — up to 25 MB)</Label>
              <Input type="file" accept={ACCEPT} className="mt-1" onChange={(e) => setFile(e.target.files?.[0] || null)}
                     data-testid="resource-form-file" /></div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="resource-form-cancel">Cancel</Button>
          <Button onClick={save} disabled={!canSave || saving} data-testid="resource-form-save">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}{resource ? "Save changes" : "Add as draft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
